import { useState, useEffect } from 'react'
import { useMutation } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { workoutPlannerAPI } from '../../services/api'
import ExerciseAvailabilityPicker from './ExerciseAvailabilityPicker'
import { InformationCircleIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline'
import { contentLanguage } from '../../i18n/supported'
import { MUSCLE_GROUPS, DIFFICULTY_LEVELS, EXERCISE_TYPES, DEFAULT_METRICS_BY_TYPE } from './exerciseOptions'

// Helper function to detect if text is likely non-English
// Part of multi-language architecture: warn users to enter English text
function detectNonEnglish(text) {
  if (!text || text.length < 3) return null

  // German indicators
  const germanPatterns = [
    /[äöüßÄÖÜ]/,  // German-specific characters
    /\b(und|oder|mit|für|bei|auf|aus|nach|von|zu|der|die|das|ein|eine|ist|sind|haben|werden|kann|muss|soll)\b/i,
    /\b(übung|muskel|brust|rücken|bein|schulter|arm|bauch|hantel|stange)\b/i,
  ]

  // Portuguese indicators
  const portuguesePatterns = [
    /[ãõçÃÕÇáéíóúâêôÁÉÍÓÚÂÊÔ]/,
    /\b(com|para|não|são|está|uma|esse|essa|isso|como|mais|também)\b/i,
  ]

  // French indicators
  const frenchPatterns = [
    /[àâçéèêëîïôùûüÿœæÀÂÇÉÈÊËÎÏÔÙÛÜŸŒÆ]/,
    /\b(avec|pour|dans|sont|cette|aussi|mais|plus|comme|tout)\b/i,
  ]

  for (const pattern of germanPatterns) {
    if (pattern.test(text)) return 'German'
  }
  for (const pattern of portuguesePatterns) {
    if (pattern.test(text)) return 'Portuguese'
  }
  for (const pattern of frenchPatterns) {
    if (pattern.test(text)) return 'French'
  }

  return null
}

// Exercise Form Modal Component
// Following multi-language architecture: English is the base language
//
// VH-268: also the review form for an AI-proposed exercise. The proposal arrives
// as `exercise` WITHOUT an id — so it is created, not updated — and every field
// (type, sets, metrics) is editable before anything is saved. `notices` are the
// proposal's warnings; `extraPayload` carries its provenance flag.
export default function ExerciseFormModal({ exercise, equipment, getEquipmentName, getEquipmentLabel, onClose, onSuccess, notices = [], extraPayload = null }) {
  const isEdit = Boolean(exercise?.id)
  const initialType = exercise?.exercise_type || 'strength'
  const { t, i18n } = useTranslation()
  const currentLanguage = contentLanguage(i18n?.language)
  const [languageWarning, setLanguageWarning] = useState(null)
  const [newMetricKey, setNewMetricKey] = useState('')
  const [newMetricLabel, setNewMetricLabel] = useState('')

  const [formData, setFormData] = useState({
    name_de: exercise?.name_en || '',  // Use English as primary, sync to _de for backwards compat
    name_en: exercise?.name_en || '',
    muscle_group: exercise?.muscle_group || '',
    secondary_muscle_groups: exercise?.secondary_muscle_groups || [],
    exercise_type: initialType,
    // A proposed non-strength exercise has no metrics yet: start from the
    // type's template, exactly as choosing that type in the form would.
    custom_metrics: exercise?.custom_metrics
      || (Object.keys(DEFAULT_METRICS_BY_TYPE[initialType] || {}).length > 0
        ? DEFAULT_METRICS_BY_TYPE[initialType]
        : null),
    equipment_id: exercise?.equipment_id || '',
    default_training_sets: exercise?.default_training_sets || 5,
    default_warmup_sets: exercise?.default_warmup_sets || 0,
    default_rest_seconds: exercise?.default_rest_seconds || 60,
    video_url: exercise?.video_url || '',
    video_language: exercise?.video_language || '',
    difficulty_level: exercise?.difficulty_level || '',
    instructions_de: exercise?.instructions_en || '',  // Use English as primary
    instructions_en: exercise?.instructions_en || '',
    is_active: exercise?.is_active ?? true
  })

  // VH-125: resolve against the equipment the form currently points at, so both
  // hints react as the athlete changes the equipment or the muscle group —
  // rather than describing whatever was linked when the form opened.
  const selectedEquipment = (equipment || []).find(
    (eq) => String(eq.id) === String(formData.equipment_id)
  )
  const selectedEquipmentName = selectedEquipment ? getEquipmentName(selectedEquipment) : ''
  const inheritedVideoUrl = selectedEquipment?.video_url || ''

  // Advisory: the exercise owns its muscle group. The equipment's list is a
  // broader claim about what the tool can train, so a disagreement means one of
  // the two records is incomplete — never a reason to block the save.
  const equipmentTrains = selectedEquipment?.primary_muscle_groups || []
  const muscleMismatch = Boolean(
    formData.muscle_group &&
      selectedEquipment &&
      equipmentTrains.length > 0 &&
      !equipmentTrains.some(
        (g) => String(g).toLowerCase() === String(formData.muscle_group).toLowerCase()
      )
  )

  // Handle exercise type change - apply default metrics for the type
  const handleExerciseTypeChange = (newType) => {
    const defaultMetrics = DEFAULT_METRICS_BY_TYPE[newType]
    setFormData(prev => ({
      ...prev,
      exercise_type: newType,
      custom_metrics: Object.keys(defaultMetrics).length > 0 ? defaultMetrics : null
    }))
  }

  // Add a custom metric
  const addCustomMetric = () => {
    if (!newMetricKey.trim() || !newMetricLabel.trim()) return
    const key = newMetricKey.toLowerCase().replace(/\s+/g, '_')
    setFormData(prev => ({
      ...prev,
      custom_metrics: {
        ...(prev.custom_metrics || {}),
        [key]: {
          label_en: newMetricLabel,
          label_de: newMetricLabel,  // User can update German label later
          type: 'number',
          required: false
        }
      }
    }))
    setNewMetricKey('')
    setNewMetricLabel('')
  }

  // Remove a custom metric
  const removeCustomMetric = (key) => {
    setFormData(prev => {
      const metrics = { ...(prev.custom_metrics || {}) }
      delete metrics[key]
      return {
        ...prev,
        custom_metrics: Object.keys(metrics).length > 0 ? metrics : null
      }
    })
  }

  // Update a custom metric property
  const updateCustomMetric = (key, property, value) => {
    setFormData(prev => ({
      ...prev,
      custom_metrics: {
        ...(prev.custom_metrics || {}),
        [key]: {
          ...(prev.custom_metrics?.[key] || {}),
          [property]: value
        }
      }
    }))
  }

  // Check if exercise type uses standard strength metrics
  const isStrengthType = formData.exercise_type === 'strength'

  // Check for non-English input when form fields change
  useEffect(() => {
    const textToCheck = `${formData.name_en} ${formData.instructions_en}`
    const detected = detectNonEnglish(textToCheck)
    setLanguageWarning(detected)
  }, [formData.name_en, formData.instructions_en])

  const mutation = useMutation({
    mutationFn: isEdit
      ? (data) => workoutPlannerAPI.updateExercise(exercise.id, data)
      : workoutPlannerAPI.createExercise,
    onSuccess: onSuccess
  })

  const handleSubmit = (e) => {
    e.preventDefault()

    const submitData = {
      ...formData,
      ...(extraPayload || {}),
      // Proposed reps per set only make sense for strength, and only while the
      // set count still matches them; otherwise the backend default applies.
      ...(!isEdit && formData.exercise_type === 'strength'
        && Array.isArray(exercise?.default_reps)
        && exercise.default_reps.length === Number(formData.default_training_sets)
        ? { default_reps: exercise.default_reps }
        : {}),
      equipment_id: formData.equipment_id ? parseInt(formData.equipment_id) : null,
      video_language: formData.video_language || null
    }

    mutation.mutate(submitData)
  }

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target
    setFormData(prev => {
      const newValue = type === 'checkbox' ? checked : value

      // For name/instructions fields, sync both _de and _en for backwards compatibility
      if (name === 'name') {
        return { ...prev, name_de: value, name_en: value }
      }
      if (name === 'instructions') {
        return { ...prev, instructions_de: value, instructions_en: value }
      }

      return { ...prev, [name]: newValue }
    })
  }

  return (
    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
      <div className="bg-surface-container rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">
            {isEdit ? t('exercises.editExercise') : t('exercises.addExercise')}
          </h2>
        </div>

        <form onSubmit={handleSubmit} className="px-6 py-4 space-y-4">
          {/* Language Warning Banner */}
          {notices.filter(Boolean).map((notice) => (
            <p
              key={notice}
              className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800"
              data-testid="exercise-form-notice"
            >
              {notice}
            </p>
          ))}

          {languageWarning && (
            <div className="bg-amber-50 border border-amber-200 rounded-md p-3">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-amber-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-amber-800">
                    {t('exercises.form.languageWarningTitle', { language: languageWarning })}
                  </h3>
                  <p className="mt-1 text-sm text-amber-700">
                    {t('exercises.form.languageWarningMessage')}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('exercises.form.name')} *
            </label>
            <input
              type="text"
              name="name"
              required
              value={formData.name_en}
              onChange={handleChange}
              placeholder={t('exercises.form.namePlaceholder')}
              className="w-full focus:border-primary-500 focus:ring-primary-500"
            />
            <p className="mt-1 text-xs text-gray-500">{t('exercises.form.nameHint')}</p>
          </div>

          {/* Muscle Group & Difficulty */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('exercises.form.muscleGroup')} *
              </label>
              <select
                name="muscle_group"
                required
                value={formData.muscle_group}
                onChange={handleChange}
                className="w-full focus:border-primary-500 focus:ring-primary-500"
              >
                <option value="">{t('exercises.selectMuscleGroup')}</option>
                {MUSCLE_GROUPS.map((group) => (
                  <option key={group} value={group}>
                    {t(`exercises.muscleGroups.${group}`)}
                  </option>
                ))}
              </select>
              {/* VH-125 slice A: advisory cross-check, never a blocker. */}
              {muscleMismatch && (
                <p className="mt-1 text-xs text-amber-700">
                  {t('exercises.muscleCheckMismatch', {
                    equipment: selectedEquipmentName,
                    muscle: t(`exercises.muscleGroups.${formData.muscle_group}`),
                    defaultValue:
                      '{{equipment}} is not recorded as training {{muscle}} — check the equipment, or the exercise.',
                  })}
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('exercises.form.difficultyLevel')} *
              </label>
              <select
                name="difficulty_level"
                required
                value={formData.difficulty_level}
                onChange={handleChange}
                className="w-full focus:border-primary-500 focus:ring-primary-500"
              >
                <option value="">{t('exercises.selectDifficulty')}</option>
                {DIFFICULTY_LEVELS.map((level) => (
                  <option key={level.value} value={level.value}>
                    {t(`exercises.difficulty.${level.value}`)}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Equipment */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('exercises.form.equipment')}
            </label>
            <select
              name="equipment_id"
              value={formData.equipment_id}
              onChange={handleChange}
              className="w-full focus:border-primary-500 focus:ring-primary-500"
            >
              <option value="">{t('exercises.noEquipment')}</option>
              {equipment.map((eq) => (
                <option key={eq.id} value={eq.id}>
                  {getEquipmentLabel(eq)}
                </option>
              ))}
            </select>
          </div>

          {/* VH-143: the other machines this exercise can be performed on.
              Only for an exercise that already exists — the links hang off its
              id, and for a NEW exercise they are created on save by
              link_primary_and_twins() anyway. */}
          {exercise?.id && <ExerciseAvailabilityPicker exerciseId={exercise.id} />}

          {/* Exercise Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('exercises.form.exerciseType')}
            </label>
            <div className="grid grid-cols-3 gap-2">
              {EXERCISE_TYPES.map((type) => (
                <button
                  key={type.value}
                  type="button"
                  onClick={() => handleExerciseTypeChange(type.value)}
                  aria-pressed={formData.exercise_type === type.value}
                  data-testid={`exercise-type-${type.value}`}
                  className={`flex items-center justify-center px-3 py-2 rounded-md border text-sm font-medium transition-colors
                    ${formData.exercise_type === type.value
                      ? 'border-primary-500 bg-primary-50 text-primary-700'
                      : 'border-gray-300 bg-surface-container text-gray-700 hover:bg-gray-50'
                    }`}
                >
                  <span className="mr-2">{type.icon}</span>
                  {t(`exercises.exerciseTypes.${type.value}`)}
                </button>
              ))}
            </div>
            <p className="mt-1 text-xs text-gray-500">{t('exercises.form.exerciseTypeHint')}</p>
          </div>

          {/* Training Parameters - Only for Strength */}
          {isStrengthType && (
            <div className="grid grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('exercises.form.trainingSets')}
                </label>
                <input
                  type="number"
                  name="default_training_sets"
                  min="1"
                  max="20"
                  value={formData.default_training_sets}
                  onChange={handleChange}
                  className="w-full focus:border-primary-500 focus:ring-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('exercises.form.warmupSets')}
                </label>
                <input
                  type="number"
                  name="default_warmup_sets"
                  min="0"
                  max="10"
                  value={formData.default_warmup_sets}
                  onChange={handleChange}
                  className="w-full focus:border-primary-500 focus:ring-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('exercises.form.restSeconds')}
                </label>
                <input
                  type="number"
                  name="default_rest_seconds"
                  min="0"
                  max="600"
                  value={formData.default_rest_seconds}
                  onChange={handleChange}
                  className="w-full focus:border-primary-500 focus:ring-primary-500"
                />
              </div>
            </div>
          )}

          {/* Custom Metrics Editor - For non-strength types */}
          {!isStrengthType && (
            <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
              <h4 className="text-sm font-medium text-gray-700 mb-3">
                {t('exercises.form.customMetrics')}
              </h4>

              {/* Existing Metrics */}
              {formData.custom_metrics && Object.keys(formData.custom_metrics).length > 0 ? (
                <div className="space-y-2 mb-4">
                  {Object.entries(formData.custom_metrics).map(([key, metric]) => (
                    <div key={key} className="flex items-center justify-between bg-surface-container p-2 rounded border border-gray-200">
                      <div className="flex-1">
                        <span className="font-medium text-sm text-gray-800">
                          {currentLanguage === 'de' ? metric.label_de : metric.label_en}
                        </span>
                        {metric.unit && (
                          <span className="text-xs text-gray-500 ml-2">({metric.unit})</span>
                        )}
                        {metric.required && (
                          <span className="text-xs text-red-500 ml-2">*</span>
                        )}
                      </div>
                      <div className="flex items-center space-x-2">
                        <input
                          type="number"
                          placeholder={t('exercises.form.defaultValue')}
                          value={metric.default || ''}
                          onChange={(e) => updateCustomMetric(key, 'default', e.target.value ? parseFloat(e.target.value) : null)}
                          className="w-20 text-sm focus:border-primary-500 focus:ring-primary-500"
                        />
                        <label
                          className="flex items-center text-xs text-gray-600 cursor-help"
                          title={t('exercises.form.requiredTooltip')}
                        >
                          <input
                            type="checkbox"
                            checked={metric.required || false}
                            onChange={(e) => updateCustomMetric(key, 'required', e.target.checked)}
                            className="text-primary-600 focus:ring-primary-500 mr-1"
                          />
                          {t('exercises.form.required')}
                          <InformationCircleIcon className="h-3.5 w-3.5 ml-1 text-gray-400" />
                        </label>
                        <button
                          type="button"
                          onClick={() => removeCustomMetric(key)}
                          className="text-red-500 hover:text-red-700"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-500 mb-4">{t('exercises.form.noCustomMetrics')}</p>
              )}

              {/* Add New Metric */}
              <div className="flex items-end space-x-2">
                <div className="flex-1">
                  <label className="block text-xs text-gray-600 mb-1">{t('exercises.form.metricKey')}</label>
                  <input
                    type="text"
                    value={newMetricKey}
                    onChange={(e) => setNewMetricKey(e.target.value)}
                    placeholder="e.g., duration_min"
                    className="w-full text-sm focus:border-primary-500 focus:ring-primary-500"
                  />
                </div>
                <div className="flex-1">
                  <label className="block text-xs text-gray-600 mb-1">{t('exercises.form.metricLabel')}</label>
                  <input
                    type="text"
                    value={newMetricLabel}
                    onChange={(e) => setNewMetricLabel(e.target.value)}
                    placeholder="e.g., Duration (min)"
                    className="w-full text-sm focus:border-primary-500 focus:ring-primary-500"
                  />
                </div>
                <button
                  type="button"
                  onClick={addCustomMetric}
                  disabled={!newMetricKey.trim() || !newMetricLabel.trim()}
                  className="px-3 py-2 bg-primary-600 text-on-primary text-sm rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <PlusIcon className="h-4 w-4" />
                </button>
              </div>
            </div>
          )}

          {/* Video URL — VH-125: the equipment is the source of record. Leaving
              this empty inherits the equipment's video, so replacing that video
              updates every exercise on it that has not overridden. Typing a URL
              here IS the override; clearing it goes back to inheriting. */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('exercises.form.videoUrl')}
            </label>
            <input
              type="url"
              name="video_url"
              value={formData.video_url}
              onChange={handleChange}
              placeholder="https://www.youtube.com/watch?v=..."
              className="w-full focus:border-primary-500 focus:ring-primary-500"
            />
            {inheritedVideoUrl && !formData.video_url && (
              <p className="mt-1 text-xs text-gray-500">
                {t('exercises.form.videoInheritedHint', {
                  equipment: selectedEquipmentName,
                  defaultValue:
                    'Leave empty to use {{equipment}}\'s video. Enter a URL to override it for this exercise.',
                })}
              </p>
            )}
            {inheritedVideoUrl && formData.video_url && (
              <button
                type="button"
                onClick={() => setFormData((prev) => ({ ...prev, video_url: '' }))}
                className="mt-1 text-xs text-primary-600 hover:text-primary-700"
              >
                {t('exercises.form.clearVideoOverride', {
                  equipment: selectedEquipmentName,
                  defaultValue: "Clear override and use {{equipment}}'s video",
                })}
              </button>
            )}
          </div>

          {/* Instructions */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('exercises.form.instructions')}
            </label>
            <textarea
              name="instructions"
              value={formData.instructions_en}
              onChange={handleChange}
              rows="3"
              placeholder={t('exercises.form.instructionsPlaceholder')}
              className="w-full focus:border-primary-500 focus:ring-primary-500"
            />
          </div>

          {/* Active */}
          <div className="flex items-center">
            <input
              type="checkbox"
              name="is_active"
              checked={formData.is_active}
              onChange={handleChange}
              className="text-primary-600 focus:ring-primary-500"
            />
            <label className="ml-2 text-sm font-medium text-gray-700">
              {t('exercises.form.isActive')}
            </label>
          </div>

          <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="btn-outline"
              disabled={mutation.isPending}
            >
              {t('common.cancel')}
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={mutation.isPending}
            >
              {mutation.isPending ? t('common.loading') : t('common.save')}
            </button>
          </div>

          {mutation.isError && (
            <div className="text-sm text-red-600 mt-2">
              {t('common.error')}: {mutation.error?.response?.data?.detail || t('exercises.saveFailed')}
            </div>
          )}
        </form>
      </div>
    </div>
  )
}
